// src/pages/MediaSpend.js 
import React, { useEffect, useMemo, useRef, useState } from "react";
import Filter_Store from "../common/Filter_store";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line
} from "recharts";
import "./MediaSpend.css";
import MobileFiltersDrawer from "../common/MobileFiltersDrawer";
import TimeRangePicker from "../common/TimeRangePicker";
import LangSwitcher from "../common/LangSwitcher";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { useDateRange } from "../../context/DateRangeContext";
import { useFilters } from "../../context/FilterContext";

const API_BASE = "https://boss.kfdtech.cn/api";
const AXIS_NONE = { axisLine: false, tickLine: false };


// K/M/B 缩写（小于 1000 取整数，大于等于 1000 缩写）
const formatAbbr = (n, digits = 2) => {
  if (n == null || isNaN(n)) return "";
  const v = Number(n);
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";

  // 用来清理小数尾零
  const trim = (x) => x.toFixed(digits).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1");

  // 1) 小于 1000 → 返回整数
  if (abs < 1000) return String(Math.round(v));

  // 2) ≥ 1K → 用 K/M/B 缩写
  if (abs >= 1e9) return `${sign}${trim(abs / 1e9)} B`;
  if (abs >= 1e6) return `${sign}${trim(abs / 1e6)} M`;
  if (abs >= 1e3) return `${sign}${trim(abs / 1e3)} K`;

  return String(v);
};
const toYm = (d) => {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};
const NoData = () => <div className="no-data">No data</div>;

/* ---------- 组件 ---------- */
export default function MediaSpend() {
  const { authFetch } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const { dateRange, setDateRange } = useDateRange();
  const { filters, setFilters } = useFilters();

  const [filterOpts, setFilterOpts] = useState({ stores: [], platforms: [], brand2plats: {} });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const initializedRef = useRef(false);

  const [cards, setCards] = useState({ salesFromMedia: 0, mediaSpend: 0, convRate: 0 });
  const [bar, setBar] = useState([]);
  const [traffic, setTraffic] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const filterLoading =
    filterOpts.stores.length === 0 && filterOpts.platforms.length === 0;

  /* ---------------- 初始化 filters（和 Stores.js 完全一致） ---------------- */
  useEffect(() => {
    let saved = sessionStorage.getItem("filters");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.stores?.length || parsed?.platforms?.length) {
          setFilters(parsed);
        }
      } catch {}
    }
  }, []);

  /* ---------------- 初始化筛选项列表 ---------------- */
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const r = await authFetch(`${API_BASE}/filters`);
        const j = await r.json();
        if (!r.ok) throw new Error(j?.detail || j?.error || `HTTP ${r.status}`);
        if (abort) return;

        const stores = j.stores || [];
        const platforms = j.platforms || [];
        const brand2plats = j.brand2plats || {};
        setFilterOpts({ stores, platforms, brand2plats });

        if (!initializedRef.current) {
          initializedRef.current = true;

          const contextHasFilters =
            (filters?.stores?.length ?? 0) > 0 ||
            (filters?.platforms?.length ?? 0) > 0;

          if (!contextHasFilters) {
            const all = {
              stores: stores.slice(),
              platforms: platforms.slice(),
            };
            setFilters(all);
            sessionStorage.setItem("filters", JSON.stringify(all));
          }
        }
      } catch (e) {
        if (!abort) setErr(String(e?.message || e));
      }
    })();
    return () => { abort = true; };
  }, []);
  /* ---------------- allowedPlatforms（与 Stores.js 相同轻量版本） ---------------- */
  const allowedPlatforms = useMemo(() => {
    const sel = filters.stores;
    if (!sel?.length) return filterOpts.platforms;

    const acc = new Set();
    sel.forEach((b) =>
      (filterOpts.brand2plats[b] || []).forEach((p) => acc.add(p))
    );
    return Array.from(acc).sort((a, b) => a.localeCompare(b));
  }, [filters.stores, filterOpts]);

  /* ---------------- 筛选改变（与 Stores.js 完全一致） ---------------- */
  const onFiltersChange = (next) => {
    if (filterLoading) return;
    setFilters(next);
    sessionStorage.setItem("filters", JSON.stringify(next));
  };

  /* ---------------- 构造请求参数 ---------------- */
  const payload = useMemo(() => {
    const start = toYm(dateRange?.startDate);
    const end = toYm(dateRange?.endDate);
    const brands = filters.stores || [];
    const plats = filters.platforms || [];

    if (!start || !end) return null;
    if (brands.length === 0 || plats.length === 0) return null;

    return {
      YearMonth_List: [start, end],
      Brand_List: brands,
      Platform_List: plats,
      allow_current_month: dateRange?.mode === "custom",
    };
  }, [dateRange, filters]);

  /* ---------------- 拉取数据 ---------------- */
  useEffect(() => {
    let abort = false;

    (async () => {
      if (filterLoading || !payload) {
        setLoading(false);
        setErr("");
        setCards({ salesFromMedia: 0, mediaSpend: 0, convRate: 0 });
        setBar([]);
        setTraffic([]);
        return;
      }

      try {
        setLoading(true);
        setErr("");

        const r = await authFetch(`${API_BASE}/mediaspend`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const j = await r.json();
        if (!r.ok) throw new Error(j?.detail || j?.error || `HTTP ${r.status}`);
        if (abort) return;

        setCards(j?.cards || { salesFromMedia: 0, mediaSpend: 0, convRate: 0 });
        setBar(j?.bar || []);
        setTraffic(j?.traffic || []);
      } catch (e) {
        if (!abort) setErr(String(e?.message || e));
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => { abort = true; };
  }, [payload, filterLoading, authFetch]);

  /* ---------------- 渲染 ---------------- */
  return (
    <div className="mediaSpend-layout">
      <div className="main-panel">
        {/* 顶栏 */}
        <div className="page-topbar">
          <h1 className="site-title">{t("title")}</h1>
          <div className="only-mobile"><LangSwitcher /></div>
        </div>

        {/* 页内标题 + 时间/移动筛选 */}
        <div className="overview-header" style={{ marginTop: 6 }}>
          <div className="page-subtitle">{t("Media Spend")}</div>
          <div className="header-right">
            <button
              type="button"
              className="filter-button-mobile"
              onClick={() => setDrawerOpen(true)}
            >
              筛选
            </button>
            <TimeRangePicker value={dateRange} onChange={setDateRange} />
          </div>
        </div>

        {/* 上方卡片 */}
        <div className="stat-cards">
          {[
            { title: t("Total Sales from Media"), value: cards.salesFromMedia },
            { title: t("Total Media Spend"), value: cards.mediaSpend },
            {
              title: t("Conversion Rate"),
              value: `${Math.round((cards.convRate || 0) * 1000) / 10}%`,
            },
          ].map((it, i) => (
            <div
              key={it.title}
              className={`card ${i % 2 === 0 ? "bg-purple" : "bg-blue"}`}
            >
              <div className="card-title">{it.title}</div>
              <div className="card-value">
                {typeof it.value === "number"
                  ? formatAbbr(it.value, 2)
                  : it.value}
              </div>
            </div>
          ))}
        </div>

        {/* Media Spend 图表 */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>{t("Media Spend (RMB)")}</h3>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="dot gray" /> {t("Media Spend")}
              </span>
              <span className="legend-item">
                <span className="dot blue" /> {t("Sales from Media")}
              </span>
            </div>
          </div>
          <div className="chart-wrap">
            {bar.length === 0 ? (
              <NoData />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={bar}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <XAxis dataKey="ym" {...AXIS_NONE} />
                  <YAxis {...AXIS_NONE} tickFormatter={(v) => formatAbbr(v, 2)} />
                  <Tooltip formatter={(v) => formatAbbr(Number(v), 2)} />
                  <Bar
                    dataKey="sales"
                    name="Sales from Media"
                    fill="#8cb4f0"
                    barSize={20}
                    radius={[10, 10, 10, 10]}
                  />
                  <Bar
                    dataKey="spending"
                    name="Media Spend"
                    fill="#b3b3b3"
                    barSize={20}
                    radius={[10, 10, 10, 10]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Traffic 图表 */}
        <div className="chart-section" style={{ marginTop: 12 }}>
          <h3>{t("Traffic")}</h3>
          <div className="chart-wrap">
            {traffic.length === 0 ? (
              <NoData />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={traffic}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <XAxis dataKey="ym" {...AXIS_NONE} />
                  <YAxis
                    {...AXIS_NONE}
                    width={80}
                    tickMargin={6}
                    tickFormatter={(v) => formatAbbr(v, 1)}
                  />
                  <Tooltip formatter={(v) => formatAbbr(Number(v), 2)} />
                  <Line
                    type="monotone"
                    dataKey="uv"
                    stroke="#8cb4f0"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="loading-card">
              <div className="spinner" />
              <div className="loading-text">Loading…</div>
            </div>
          </div>
        )}

        {err && !loading && (
          <div className="loading-overlay">
            <div className="loading-card">
              <div className="loading-text">Error: {err}</div>
            </div>
          </div>
        )}

        <div className="bottom-spacer" aria-hidden="true" />
      </div>

      {/* 右侧筛选栏 */}
      <div className="filters-sidebar">
        <div className="filters-head">
          <LangSwitcher />
        </div>
        <Filter_Store
          loading={filterLoading}
          stores={filterOpts.stores}
          platforms={allowedPlatforms}
          value={filters}
          onChange={onFiltersChange}
        />
      </div>

      {/* 移动端抽屉 */}
      <MobileFiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Filter_Store
          loading={filterLoading}
          stores={filterOpts.stores}
          platforms={allowedPlatforms}
          value={filters}
          onChange={onFiltersChange}
        />
      </MobileFiltersDrawer>
    </div>
  );
}
